import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { inspectionRoutesService } from "../services/inspectionRoutesService";
import styles from "../assets/inspectionRouteCreate.module.css";

const InspectionRouteCreate = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    building_manager_id: "",
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [errors, setErrors] = useState({});
  const [buildingManagers, setBuildingManagers] = useState([]);

  // Load building managers on component mount
  useEffect(() => {
    loadBuildingManagers();
  }, []);

  // Load building managers
  const loadBuildingManagers = async () => {
    try {
      if (useMockData) {
        const mockBuildingManagers =
          inspectionRoutesService.getMockBuildingManagers();
        setBuildingManagers(mockBuildingManagers || []);
      } else {
        try {
          const response = await inspectionRoutesService.getBuildingManagers();
          console.log("Building managers API response:", response);
          console.log("Response data:", response.data);
          console.log("Response data type:", typeof response.data);

          // Ensure we have an array, handle different response structures
          const managers = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data?.building_managers)
            ? response.data.building_managers
            : [];
          console.log("Processed managers:", managers);
          setBuildingManagers(managers);
        } catch (apiError) {
          console.error("API call failed for building managers:", apiError);
          // Fallback to mock data
          const mockBuildingManagers =
            inspectionRoutesService.getMockBuildingManagers();
          setBuildingManagers(mockBuildingManagers || []);
        }
      }
    } catch (error) {
      console.error("Error loading building managers:", error);
      // Use mock data as fallback
      const mockBuildingManagers =
        inspectionRoutesService.getMockBuildingManagers();
      setBuildingManagers(mockBuildingManagers || []);
    }
  };

  // Reload data when mock data toggle changes
  useEffect(() => {
    loadBuildingManagers();
  }, [useMockData]);

  // Debug formData changes
  useEffect(() => {
    console.log("Form data updated:", formData);
  }, [formData]);

  // Debug buildingManagers changes
  useEffect(() => {
    console.log("Building managers updated:", buildingManagers);
    console.log("Building managers type:", typeof buildingManagers);
    console.log("Is array:", Array.isArray(buildingManagers));
  }, [buildingManagers]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length > 255) {
      newErrors.name = "Name must not exceed 255 characters";
    }

    if (!formData.building_manager_id) {
      newErrors.building_manager_id = "Building Manager is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (useMockData) {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));
        toast.success("Inspection Route created successfully! (mock)");
      } else {
        await inspectionRoutesService.createInspectionRoute(formData);
        toast.success("Inspection Route created successfully!");
      }

      // Redirect to index page
      navigate("/inspection-routes");
    } catch (error) {
      console.error("Error creating inspection route:", error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error("Failed to create inspection route. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/inspection-routes");
  };

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/inspection-routes">Inspection Routes</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Create</span>
      </div>

      {/* Mock Data Toggle (for development) */}
      <div className={styles.mockDataToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
        <button
          type="button"
          onClick={() => {
            console.log("Force reloading data...");
            loadBuildingManagers();
          }}
          style={{
            marginLeft: "10px",
            padding: "2px 8px",
            fontSize: "12px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "3px",
            cursor: "pointer"
          }}
        >
          Reload Data
        </button>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Create Inspection Route</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <fieldset className={styles.formHorizontal}>
                  {/* Name Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.name ? styles.hasError : ""
                        }`}
                        required
                        placeholder="Enter inspection route name"
                      />
                      {errors.name && (
                        <div className={styles.errorMessage}>{errors.name}</div>
                      )}
                    </div>
                  </div>

                  {/* Building Manager Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Building Manager
                    </label>
                    <div className={styles.colSm5}>
                      <select
                        name="building_manager_id"
                        value={formData.building_manager_id || ""}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.building_manager_id ? styles.hasError : ""
                        }`}
                        required
                      >
                        <option value="">Please select</option>
                        {Array.isArray(buildingManagers) &&
                          buildingManagers.map((manager) => (
                            <option key={manager.id} value={manager.id}>
                              {manager.full_name}
                            </option>
                          ))}
                      </select>
                      {errors.building_manager_id && (
                        <div className={styles.errorMessage}>
                          {errors.building_manager_id}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button - Full width as per Laravel create form */}
                  <div className={styles.formGroup}>
                    <div className={styles.colSm3}></div>
                    <div className={styles.colSm12}>
                      <button
                        style={{ backgroundColor: "#1B2046" }}
                        type="submit"
                        className={styles.btnHead}
                        disabled={loading}
                      >
                        {loading ? "Creating..." : "Create"}
                      </button>
                      <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionRouteCreate; 